const STREET_TOKENS = {
    street: 'st',
    str: 'st',
    avenue: 'ave',
    av: 'ave',
    boulevard: 'blvd',
    drive: 'dr',
    road: 'rd',
    court: 'ct',
    lane: 'ln',
    place: 'pl',
    circle: 'cir',
    highway: 'hwy',
    parkway: 'pkwy',
    terrace: 'ter',
    trail: 'trl',
    square: 'sq',
    north: 'n',
    south: 's',
    east: 'e',
    west: 'w',
    northeast: 'ne',
    northwest: 'nw',
    southeast: 'se',
    southwest: 'sw',
    apartment: 'apt',
    apt: 'apt',
    suite: 'ste',
    unit: 'unit',
    floor: 'fl',
}

const US_STATE_ABBREV = {
    alabama: 'al',
    alaska: 'ak',
    arizona: 'az',
    arkansas: 'ar',
    california: 'ca',
    colorado: 'co',
    connecticut: 'ct',
    delaware: 'de',
    florida: 'fl',
    georgia: 'ga',
    hawaii: 'hi',
    idaho: 'id',
    illinois: 'il',
    indiana: 'in',
    iowa: 'ia',
    kansas: 'ks',
    kentucky: 'ky',
    louisiana: 'la',
    maine: 'me',
    maryland: 'md',
    massachusetts: 'ma',
    michigan: 'mi',
    minnesota: 'mn',
    mississippi: 'ms',
    missouri: 'mo',
    montana: 'mt',
    nebraska: 'ne',
    nevada: 'nv',
    'new hampshire': 'nh',
    'new jersey': 'nj',
    'new mexico': 'nm',
    'new york': 'ny',
    'north carolina': 'nc',
    'north dakota': 'nd',
    ohio: 'oh',
    oklahoma: 'ok',
    oregon: 'or',
    pennsylvania: 'pa',
    'rhode island': 'ri',
    'south carolina': 'sc',
    'south dakota': 'sd',
    tennessee: 'tn',
    texas: 'tx',
    utah: 'ut',
    vermont: 'vt',
    virginia: 'va',
    washington: 'wa',
    'west virginia': 'wv',
    wisconsin: 'wi',
    wyoming: 'wy',
    'district of columbia': 'dc',
}

const DISMISSED_OVERRIDE_REASON_PATTERNS = [
    /alert(?:s)?\s+(?:is|are|was|were)\s+(?:incorrect|wrong|false|an?\s+error)/i,
    /this\s+is\s+(?:her|his|their|the\s+patient'?s?)\s+address/i,
    /address\s+matches(?:\s+on\s+file)?/i,
    /matches\s+on\s+file/i,
    /error\s+correct/i,
    /address\s+is\s+correct/i,
    /same\s+address(?:\s+on\s+file)?/i,
]

export function formatPatientHomeAddress(contact) {
    if (!contact) {
        return ''
    }
    const parts = [
        contact.address_1,
        contact.address_2,
        contact.city,
        contact.state,
        contact.zip,
    ].filter((part) => `${part ?? ''}`.trim())
    return parts.join(', ')
}

export function normalizeRideAddress(value) {
    let text = `${value ?? ''}`.toLowerCase()
    if (!text.trim()) {
        return ''
    }

    text = text.replace(/[.#,/]/g, ' ')
    text = text.replace(/\bunited states of america\b|\bunited states\b|\busa\b|\bu\.s\.a\.?\b/g, ' ')
    text = text.replace(/\b(\d{5})-\d{4}\b/g, '$1')
    text = text.replace(/\b(apartment|apt|unit|suite|ste)\b/g, 'apt')
    text = text.replace(/\s+/g, ' ').trim()

    Object.keys(US_STATE_ABBREV).forEach((name) => {
        if (name.includes(' ')) {
            text = text.replace(new RegExp(`\\b${name}\\b`, 'g'), US_STATE_ABBREV[name])
        }
    })

    return text
        .split(' ')
        .map((token) => STREET_TOKENS[token] || US_STATE_ABBREV[token] || token)
        .filter((token) => token && token !== 'us')
        .join(' ')
}

export function addressesLookTheSame(left, right) {
    const a = normalizeRideAddress(left)
    const b = normalizeRideAddress(right)
    if (!a || !b) {
        return false
    }
    if (a === b) {
        return true
    }
    if (a.includes(b) || b.includes(a)) {
        return true
    }

    const tokensA = new Set(a.split(' '))
    const tokensB = new Set(b.split(' '))
    const smaller = tokensA.size <= tokensB.size ? tokensA : tokensB
    const larger = tokensA.size <= tokensB.size ? tokensB : tokensA
    if (smaller.size < 3) {
        return false
    }
    let matched = 0
    smaller.forEach((token) => {
        if (larger.has(token)) {
            matched += 1
        }
    })
    return matched / smaller.size >= 0.85
}

export function rideAddressMatchesPatient(rideAddress, patientContact) {
    const patientAddress = formatPatientHomeAddress(patientContact)
    if (addressesLookTheSame(rideAddress, patientAddress)) {
        return true
    }

    const withoutLine2 = [
        patientContact?.address_1,
        patientContact?.city,
        patientContact?.state,
        patientContact?.zip,
    ].filter((part) => `${part ?? ''}`.trim()).join(', ')

    return addressesLookTheSame(rideAddress, withoutLine2)
}

export function isDismissedAddressOverrideReason(reason) {
    const text = `${reason ?? ''}`.trim()
    if (!text) {
        return false
    }
    return DISMISSED_OVERRIDE_REASON_PATTERNS.some((pattern) => pattern.test(text))
}

export function shouldShowAddressOverrideAlert({ reason, rideAddress, patientContact } = {}) {
    if (!`${reason ?? ''}`.trim() || !`${rideAddress ?? ''}`.trim()) {
        return false
    }
    if (isDismissedAddressOverrideReason(reason)) {
        return false
    }
    if (patientContact && rideAddressMatchesPatient(rideAddress, patientContact)) {
        return false
    }
    return true
}
